import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faRotate, faKey, faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import {
  customerService,
  type Customer,
  type CreateCustomerPayload,
  type CustomerCreatedResponse,
} from '@/services/customer.service';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';
import { getApiErrorMessage } from '@/lib/api-error';
import { ValidatedField } from './ValidatedField';
import { MathCaptcha } from './MathCaptcha';
import { LocationContactsManager } from './LocationContactsManager';

type FormState = {
  name: string;
  customerCode: string;
  organizationName: string;
  email: string;
  phone: string;
  alternatePhone: string;
  gstNumber: string;
  website: string;
  industry: string;
  notes: string;
  username: string;
  temporaryPassword: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
};

const empty: FormState = {
  name: '', customerCode: '', organizationName: '', email: '', phone: '', alternatePhone: '',
  gstNumber: '', website: '', industry: '', notes: '', username: '', temporaryPassword: '',
  addressLine1: '', addressLine2: '', city: '', state: '', country: '', postalCode: '',
};

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="border-b border-border pb-1.5 text-sm font-semibold text-foreground">{children}</h3>;
}

export function CustomerFormModal({
  open,
  onClose,
  editTarget,
  onSaved,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  editTarget: Customer | null;
  onSaved: () => void;
  /** Fired once, immediately after a *new* customer is created, with the full
   * created record. Lets callers (e.g. the Register Equipment dialog) refresh a
   * customer dropdown and auto-select the new customer without waiting for the
   * modal's one-time-credentials step to be dismissed. */
  onCreated?: (customer: CustomerCreatedResponse) => void;
}) {
  const isEdit = !!editTarget;
  const [f, setF] = useState<FormState>(empty);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [step, setStep] = useState<'details' | 'locations'>('details');
  const [created, setCreated] = useState<CustomerCreatedResponse | null>(null);

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setF((p) => ({ ...p, [k]: e.target.value }));
  const setVal = (k: keyof FormState) => (v: string) => setF((p) => ({ ...p, [k]: v }));

  // Initialise on open
  useEffect(() => {
    if (!open) return;
    setStep('details');
    setCreated(null);
    setCaptchaAnswer('');
    if (editTarget) {
      setF({
        name: editTarget.name, customerCode: editTarget.customerCode ?? '',
        organizationName: editTarget.organizationName ?? '', email: editTarget.email ?? '',
        phone: editTarget.phone ?? '', alternatePhone: editTarget.alternatePhone ?? '',
        gstNumber: editTarget.gstNumber ?? '', website: editTarget.website ?? '',
        industry: editTarget.industry ?? '', notes: editTarget.notes ?? '',
        username: editTarget.username ?? '', temporaryPassword: '',
        addressLine1: editTarget.addressLine1 ?? '', addressLine2: editTarget.addressLine2 ?? '',
        city: editTarget.city ?? '', state: editTarget.state ?? '', country: editTarget.country ?? '',
        postalCode: editTarget.postalCode ?? '',
      });
    } else {
      setF(empty);
      // Auto-generate code + password for a new customer
      void customerService.generateCode().then((customerCode) => setF((p) => ({ ...p, customerCode })));
      void customerService.generatePassword().then((temporaryPassword) => setF((p) => ({ ...p, temporaryPassword })));
    }
  }, [open, editTarget]);

  const regenUsername = async () => {
    if (!f.organizationName.trim()) return toast.error('Enter an organization name first');
    const username = await customerService.generateUsername(f.organizationName.trim());
    setF((p) => ({ ...p, username }));
  };
  const regenPassword = async () => {
    const temporaryPassword = await customerService.generatePassword();
    setF((p) => ({ ...p, temporaryPassword }));
  };

  const createM = useMutation({
    mutationFn: (payload: CreateCustomerPayload) => customerService.create(payload),
    onSuccess: (res) => {
      toast.success('Customer created');
      setCreated(res);
      setStep('locations');
      onSaved();
      onCreated?.(res);
    },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Failed to create customer')),
  });

  const updateM = useMutation({
    mutationFn: () => customerService.update(editTarget!.id, {
      name: f.name, customerCode: f.customerCode, organizationName: f.organizationName,
      email: f.email ? f.email.toLowerCase().trim() : undefined, phone: f.phone || undefined, alternatePhone: f.alternatePhone || undefined,
      gstNumber: f.gstNumber || undefined, website: f.website || undefined, industry: f.industry || undefined,
      notes: f.notes || undefined, addressLine1: f.addressLine1 || undefined, addressLine2: f.addressLine2 || undefined,
      city: f.city || undefined, state: f.state || undefined, country: f.country || undefined, postalCode: f.postalCode || undefined,
    }),
    onSuccess: () => { toast.success('Customer updated'); onSaved(); onClose(); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Failed to update customer')),
  });

  const submitDetails = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!f.name.trim() || !f.organizationName.trim() || !f.customerCode.trim()) {
      return toast.error('Organization Name, Contact Person and Customer Code are required');
    }
    if (isEdit) { updateM.mutate(); return; }
    if (!captchaToken || !captchaAnswer.trim()) return toast.error('Complete the security check');
    createM.mutate({
      name: f.name, customerCode: f.customerCode, organizationName: f.organizationName,
      username: f.username.trim().toLowerCase() || undefined, temporaryPassword: f.temporaryPassword || undefined,
      email: f.email ? f.email.toLowerCase().trim() : undefined, phone: f.phone || undefined, alternatePhone: f.alternatePhone || undefined,
      gstNumber: f.gstNumber || undefined, website: f.website || undefined, industry: f.industry || undefined,
      notes: f.notes || undefined, addressLine1: f.addressLine1 || undefined, addressLine2: f.addressLine2 || undefined,
      city: f.city || undefined, state: f.state || undefined, country: f.country || undefined, postalCode: f.postalCode || undefined,
      captchaToken, captchaAnswer: captchaAnswer.trim(),
    });
  };

  const saving = createM.isPending || updateM.isPending;
  const excludeId = editTarget?.id;

  // ── Step 2: post-create locations + temp password banner ──
  if (open && step === 'locations' && created) {
    return (
      <Modal open={open} onClose={onClose} title={`Customer ${created.customerCode} created`} size="2xl">
        <div className="space-y-5">
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">
            <div className="flex items-center gap-2 font-medium text-emerald-700">
              <FontAwesomeIcon icon={faCircleCheck} /> Credentials (shown once — copy now)
            </div>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="text-sm"><span className="text-muted-foreground">Username:</span> <span className="font-mono font-semibold">{created.username}</span></div>
              <div className="text-sm"><span className="text-muted-foreground">Temp Password:</span> <span className="font-mono font-semibold">{created.temporaryPassword}</span></div>
            </div>
          </div>
          <LocationContactsManager customerId={created.id} canEdit />
          <div className="flex justify-end pt-2">
            <Button type="button" onClick={onClose}>Done</Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? 'Edit Customer' : 'Add Customer'} size="2xl">
      {/* Intentionally a <div>, NOT a <form>. In edit mode this container renders
          the Service Locations manager (section D), whose LocationForm is itself a
          <form>. A wrapping <form> here would nest those forms — the inner "Save
          Location" submit then triggers a native submission of the outer form
          (page reload, no /locations POST, location never saved). Submit is driven
          by the footer button's onClick instead. */}
      <div className="space-y-6">
        {/* A. Customer Information */}
        <section className="space-y-3">
          <SectionTitle>A. Customer Information</SectionTitle>
          <div className="grid grid-cols-1 gap-x-5 gap-y-1 md:grid-cols-3">
            <FormField label="Organization Name" htmlFor="cf-org" required>
              <Input id="cf-org" value={f.organizationName} onChange={set('organizationName')} disabled={saving} />
            </FormField>
            <FormField label="Contact Person" htmlFor="cf-name" required>
              <Input id="cf-name" value={f.name} onChange={set('name')} disabled={saving} />
            </FormField>
            <ValidatedField label="Customer Code" id="cf-code" field="customer-code" value={f.customerCode} onChange={setVal('customerCode')} excludeId={excludeId} required disabled={saving} />
            <ValidatedField label="Email" id="cf-email" field="email" type="email" value={f.email} onChange={setVal('email')} excludeId={excludeId} disabled={saving} />
            <ValidatedField label="Phone" id="cf-phone" field="phone" value={f.phone} onChange={setVal('phone')} excludeId={excludeId} disabled={saving} />
            <FormField label="Alternate Phone" htmlFor="cf-aphone">
              <Input id="cf-aphone" value={f.alternatePhone} onChange={set('alternatePhone')} disabled={saving} />
            </FormField>
            <ValidatedField label="GST Number" id="cf-gst" field="gst" value={f.gstNumber} onChange={setVal('gstNumber')} excludeId={excludeId} disabled={saving} />
            <FormField label="Website" htmlFor="cf-web">
              <Input id="cf-web" value={f.website} onChange={set('website')} disabled={saving} />
            </FormField>
            <FormField label="Industry" htmlFor="cf-ind">
              <Input id="cf-ind" value={f.industry} onChange={set('industry')} disabled={saving} />
            </FormField>
          </div>
          <FormField label="Notes" htmlFor="cf-notes">
            <Textarea id="cf-notes" value={f.notes} onChange={set('notes')} rows={2} disabled={saving} />
          </FormField>
        </section>

        {/* B. Credentials */}
        <section className="space-y-3">
          <SectionTitle>B. Credentials</SectionTitle>
          <div className="grid grid-cols-1 gap-x-5 gap-y-1 md:grid-cols-2">
            <div>
              <ValidatedField label="Username" id="cf-username" field="username" value={f.username} onChange={setVal('username')} excludeId={excludeId} disabled={saving || isEdit} />
              {!isEdit && (
                <button type="button" onClick={regenUsername} className="text-xs font-medium text-primary hover:underline">
                  <FontAwesomeIcon icon={faRotate} className="mr-1 h-3 w-3" /> Generate from organization
                </button>
              )}
            </div>
            {!isEdit && (
              <FormField label="Temporary Password" htmlFor="cf-pass">
                <div className="flex items-center gap-2">
                  <Input id="cf-pass" value={f.temporaryPassword} onChange={set('temporaryPassword')} disabled={saving} className="font-mono" />
                  <button type="button" onClick={regenPassword} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted" title="Regenerate password">
                    <FontAwesomeIcon icon={faKey} className="h-3.5 w-3.5" />
                  </button>
                </div>
              </FormField>
            )}
          </div>
          {isEdit && (
            <p className="text-xs text-muted-foreground">Use “Reset Password” on the list/detail page to issue a new password.</p>
          )}
        </section>

        {/* C. Address */}
        <section className="space-y-3">
          <SectionTitle>C. Address</SectionTitle>
          <div className="grid grid-cols-1 gap-x-5 gap-y-1 md:grid-cols-3">
            <FormField label="Address Line 1" htmlFor="cf-a1"><Input id="cf-a1" value={f.addressLine1} onChange={set('addressLine1')} disabled={saving} /></FormField>
            <FormField label="Address Line 2" htmlFor="cf-a2"><Input id="cf-a2" value={f.addressLine2} onChange={set('addressLine2')} disabled={saving} /></FormField>
            <FormField label="City" htmlFor="cf-city"><Input id="cf-city" value={f.city} onChange={set('city')} disabled={saving} /></FormField>
            <FormField label="State" htmlFor="cf-state"><Input id="cf-state" value={f.state} onChange={set('state')} disabled={saving} /></FormField>
            <FormField label="Country" htmlFor="cf-country"><Input id="cf-country" value={f.country} onChange={set('country')} disabled={saving} /></FormField>
            <FormField label="Postal Code" htmlFor="cf-pc"><Input id="cf-pc" value={f.postalCode} onChange={set('postalCode')} disabled={saving} /></FormField>
          </div>
        </section>

        {/* D. Service Locations (edit only — needs an existing customer) */}
        {isEdit && editTarget && (
          <section className="space-y-3">
            <SectionTitle>D. Service Locations</SectionTitle>
            <LocationContactsManager customerId={editTarget.id} canEdit />
          </section>
        )}

        {/* Captcha (create only) */}
        {!isEdit && (
          <section className="space-y-3">
            <SectionTitle>Security</SectionTitle>
            <MathCaptcha answer={captchaAnswer} onAnswerChange={setCaptchaAnswer} onTokenChange={setCaptchaToken} disabled={saving} />
          </section>
        )}

        <div className="flex justify-end gap-3 border-t border-border pt-4">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="button" onClick={() => submitDetails()} disabled={saving} className="min-w-[140px]">
            {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : isEdit ? 'Save Changes' : 'Create Customer'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
